import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Custom RouteReuseStrategy that caches components marked with
 * `data: { reuseComponent: true }` in their route configuration.
 *
 * When navigating away from such a route, the component is detached
 * and stored. When navigating back, the stored component is reattached
 * with its full state (AG-Grid filters, sorting, scroll position, etc.).
 */
export class ReusableRouteStrategy implements RouteReuseStrategy {
  private readonly cache = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data['reuseComponent'] === true;
  }

  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null,
  ): void {
    const key = this.getRouteKey(route);
    if (handle) {
      this.cache.set(key, handle);
    } else {
      this.cache.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.cache.has(this.getRouteKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.cache.get(this.getRouteKey(route)) ?? null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot.reduce((acc, curr) => {
      if (curr.url.length > 0) {
        acc = acc.concat(curr.url.map((seg) => seg.path).join('/'));
      }
      return acc;
    }, '');
  }
}
